import { CheckCircleOutlineOutlined, Close } from "@mui/icons-material";
import {
    styled,
    Box,
    Card,
    Button,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { AccountModal } from "../accountPieces";
import { AccountInputBox } from "../components/TableActions";
import { accountInputBoxCSS } from "../../subComps/loginPieces";
import { Axios, buttonGradient, formatMoney, formatPhone, svgGradient } from "../../../common/utils";
import { baseApi } from "../../../common/constants";
import { cashbackFieldRules, LiLink } from "../../../common/commonComps";
import { GradientTop, ModalButtonWrap, SecondaryButton } from "../components/pieces";
import { Span } from "../components/Typography";
import PayPal from "../images/PayPal_2022_nomargin.svg";
import Venmo from "../images/Blue.webp";
import type { Size, AppContext } from "../../../types";
import type { ValidationError } from "yup";

const Heading = styled(Box)(() => ({
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 16,
}));

const TextLine = styled(Box)(() => ({
    fontSize: 16,
    fontWeight: 400,
    marginBottom: 16,
}));

const CB = styled(Span)(() => ({
    fontSize: 20,
    fontWeight: 600,
}));

const Panel = styled(Box)<{ opacity: number; size: Size }>(({ opacity, size }) => ({
    opacity,
    position: `absolute`,
    display: `flex`,
    flexDirection: `column`,
    minHeight: size === `small` ? `100%` : `calc(100% - 10px)`,
    zIndex: 10 + opacity,
    width: `100%`,
    padding: `40px 30px 30px`,
    transition: `opacity .6s ease`,
}));

const ConfirmBox = styled(Box)<{ size: Size }>(({ size }) => ({
    padding: size === `small` ? 0 : `0 50px`,
}));

const LeftSide = styled(Box)(() => ({
    marginBottom: 16,
    display: `inline-block`,
    fontWeight: 600,
    fontSize: 16,
}));

const RightSide = styled(Box)(() => ({
    fontWeight: 400,
    fontSize: 16,
    float: `right`,
}));

const InputBox = styled(AccountInputBox)(accountInputBoxCSS);

type CbDataRecord = Record<string, string | number | null | undefined>;

interface RequestCashBackProps {
    size: Size;
    context: AppContext;
    cbData: CbDataRecord | null;
    setCBData: (data: CbDataRecord | null) => void;
    refreshContext: () => Promise<void>;
}

const RequestCashBack = ({
    size,
    context,
    cbData = {},
    setCBData = () => {},
    refreshContext,
}: RequestCashBackProps) => {
    const [panel, setPanel] = useState(1);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [cashBack, method, email, phone] = cashbackFieldRules.fields;
    const theme = useTheme();
    const closeAll = () => {
        setPanel(1);
        setErrors({});
        setCBData(null);
    };

    useEffect(() => {
        if (cbData && Object.keys(cbData).length === 0) {
            setCBData({
                offset: new Date().getTimezoneOffset(),
                [cashBack.v]: formatMoney(context?.user?.redeemableAmt as number, { noDollarSign: true }),
                [email.v]:
                    context?.user?.settings?.[`mall-payments`]?.[`paypal-email`] ??
                    context?.user.lastEmailRedeem ??
                    context?.user?.email,
                [phone.v]: formatPhone(
                    context?.user?.settings?.[`mall-payments`]?.[`venmo-phone`] ?? context?.user.lastPhoneRedeem ?? ``
                ),
                method: context?.user?.settings?.[`mall-payments`]?.paypal === false ? `Venmo` : `PayPal`,
            });
        }
    }, [cbData]);

    const submitCashBack = () => {
        setLoading(true);
        Axios.put(`${baseApi}cashback`, cbData)
            .then(refreshContext)
            .then(() => {
                setLoading(false);
                setPanel(3);
            })
            .catch(() => {
                setLoading(false);
                toast.error(`Sorry something went wrong`);
                closeAll();
            });
    };

    return (
        <AccountModal
            sx={{
                height:
                    size === `small`
                        ? `100%`
                        : (context?.user?.redeemableAmt as number) <
                            (context?.adminConfig?.mall?.CORE_SETTINGS?.payout_threshold as number)
                          ? 300
                          : 600,
                borderRadius: size === `small` ? 0 : 10,
                overflow: `auto`,
            }}
            onClose={closeAll}
            size={size}
            enableScrollLock
            open={!!cbData}
        >
            <Card sx={{ boxSizing: `border-box` }} className="modalContent">
                {size === `small` && (
                    <Box p={2} sx={{ textAlign: `center` }}>
                        <Box component="img" sx={{ width: 150 }} src={context?.adminConfig?.mall?.Color_Desktop_Logo} />
                    </Box>
                )}
                <Close
                    sx={{
                        position: `absolute`,
                        top: 24,
                        right: 10,
                        zIndex: 30,
                        cursor: `pointer`,
                        opacity: 0.55,
                        height: 25,
                        width: 25,
                        zindex: 30,
                    }}
                    onClick={closeAll}
                />
                {size !== `small` && <GradientTop />}
                {panel === 1 && (
                    <Panel size={size} opacity={Number(panel === 1)}>
                        <Heading>Request Payout</Heading>
                        <TextLine sx={{ marginBottom: `35px` }}>
                            You have <CB>{formatMoney(context?.user?.redeemableAmt || 0)}</CB>
                            {` `}available.
                        </TextLine>
                        {(context?.user?.redeemableAmt as number) <
                        (context?.adminConfig?.mall?.CORE_SETTINGS?.payout_threshold as number) ? (
                            <>
                                <TextLine>
                                    You&apos;re on your way! Cash back redemptions become available when your balance
                                    reaches{" "}
                                    {formatMoney(context?.adminConfig?.mall?.CORE_SETTINGS?.payout_threshold as number)}{" "}
                                    or more.
                                </TextLine>
                                <ModalButtonWrap>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={closeAll}
                                        sx={{
                                            background: buttonGradient(theme),
                                            padding: `4px 8px`,
                                            minWidth: `unset`,
                                            borderRadius: 2,
                                            "&.Mui-disabled": {
                                                background: `#999 !important`,
                                                opacity: 0.5,
                                            },
                                        }}
                                    >
                                        Ok
                                    </Button>
                                </ModalButtonWrap>
                            </>
                        ) : (
                            <>
                                <FormControl
                                    sx={{
                                        width: `100%`,
                                        "& .MuiFormLabel-root": {
                                            color: `#666`,
                                        },
                                        position: `relative`,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: `flex`,
                                            justifyContent: `space-between`,
                                        }}
                                    >
                                        <FormLabel
                                            sx={{ fontSize: 16, fontWeight: 600, color: `#333` }}
                                            htmlFor={cashBack.v}
                                        >
                                            {cashBack.l}
                                        </FormLabel>
                                    </Box>
                                    <InputBox
                                        name={cashBack.v}
                                        type="text"
                                        id={cashBack.v}
                                        fullWidth
                                        onFocus={() =>
                                            setCBData({
                                                ...cbData,
                                                [cashBack.v]: ``,
                                            })
                                        }
                                        onBlur={v =>
                                            setCBData({
                                                ...cbData,
                                                [cashBack.v]: formatMoney(
                                                    parseFloat(v.target.value) > Number(context.user.redeemableAmt)
                                                        ? (context.user.redeemableAmt as number)
                                                        : v.target.value,
                                                    { noDollarSign: true }
                                                ),
                                            })
                                        }
                                        helperText={errors[cashBack.v]}
                                        error={!!errors[cashBack.v]}
                                        value={cbData?.[cashBack.v]}
                                        sx={{
                                            height: `48px`,
                                            maxWidth: `unset`,

                                            input: { fontWeight: 500, height: `23px`, paddingLeft: `1.3em` },
                                            marginTop: `5px`,
                                        }}
                                        onChange={v => {
                                            setErrors({});
                                            const newVal = v.target.value
                                                .replace(/[^0-9.]/g, ``)
                                                .replace(/(?<=\..*)\./g, ``)
                                                .replace(/(?<=\.\d{2})\d+/g, ``);
                                            if (parseFloat(newVal) > Number(context?.user?.redeemableAmt)) {
                                                return;
                                            }
                                            setCBData({
                                                ...cbData,
                                                [cashBack.v]: newVal,
                                            });
                                        }}
                                    />
                                    <Box sx={{ position: `absolute`, top: `38px`, left: `12px` }}>$</Box>
                                </FormControl>
                                <FormControl>
                                    <Box
                                        sx={{
                                            display: `flex`,
                                            justifyContent: `space-between`,
                                        }}
                                    >
                                        <FormLabel
                                            sx={{
                                                fontSize: 16,
                                                fontWeight: 600,
                                                color: `#333`,
                                                "&.MuiFormLabel-root.Mui-focused": {
                                                    color: `#333`,
                                                },
                                            }}
                                            htmlFor={method.v}
                                        >
                                            {method.l}
                                        </FormLabel>
                                    </Box>
                                    <RadioGroup
                                        sx={{ display: `block`, mb: 3 }}
                                        onChange={v => {
                                            setErrors({});
                                            setCBData({ ...cbData, method: v.target.value });
                                        }}
                                        aria-labelledby="radio-buttons-group-label"
                                        value={cbData?.method}
                                        name="radio-buttons-group"
                                    >
                                        <FormControlLabel
                                            sx={{ marginRight: 0, verticalAlign: `middle` }}
                                            value="PayPal"
                                            control={<Radio />}
                                            label={
                                                <Box
                                                    mr={4.5}
                                                    sx={{
                                                        height: 25,
                                                        verticalAlign: `middle`,
                                                        cursor: `pointer`,
                                                    }}
                                                    component="img"
                                                    src={PayPal}
                                                />
                                            }
                                        />
                                        <FormControlLabel
                                            sx={{ marginRight: 0, verticalAlign: `middle` }}
                                            value="Venmo"
                                            control={<Radio />}
                                            label={
                                                <Box
                                                    sx={{
                                                        height: 15,
                                                        verticalAlign: `middle`,
                                                        cursor: `pointer`,
                                                    }}
                                                    component="img"
                                                    src={Venmo}
                                                />
                                            }
                                        />
                                    </RadioGroup>
                                </FormControl>

                                {!cbData?.method ||
                                    (cbData?.method === `PayPal` && (
                                        <FormControl
                                            sx={{
                                                width: `100%`,
                                                "& .MuiFormLabel-root": {
                                                    color: `#666`,
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: `flex`,
                                                    justifyContent: `space-between`,
                                                }}
                                            >
                                                <FormLabel
                                                    sx={{ fontSize: 16, fontWeight: 600, color: `#333` }}
                                                    htmlFor={email.v}
                                                >
                                                    {email.l}
                                                </FormLabel>
                                            </Box>
                                            <InputBox
                                                name={email.v}
                                                type="text"
                                                id={email.v}
                                                fullWidth
                                                onFocus={() => setCBData({ ...cbData, [email.v]: `` })}
                                                helperText={errors[email.v]}
                                                error={!!errors[email.v]}
                                                value={cbData?.[email.v]}
                                                sx={{
                                                    height: `48px`,
                                                    maxWidth: `unset`,
                                                    "& .MuiInputBase-root": { padding: `0 !important` },
                                                    input: { fontWeight: 500, height: `23px` },
                                                    marginTop: `5px`,
                                                }}
                                                onChange={v => {
                                                    setErrors({});
                                                    setCBData({ ...cbData, [email.v]: v.target.value });
                                                }}
                                            />
                                        </FormControl>
                                    ))}

                                {!cbData?.method ||
                                    (cbData?.method === `Venmo` && (
                                        <FormControl
                                            sx={{
                                                width: `100%`,
                                                "& .MuiFormLabel-root": {
                                                    color: `#666`,
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: `flex`,
                                                    justifyContent: `space-between`,
                                                }}
                                            >
                                                <FormLabel
                                                    sx={{ fontSize: 16, fontWeight: 600, color: `#333` }}
                                                    htmlFor={phone.v}
                                                >
                                                    {phone.l}
                                                </FormLabel>
                                            </Box>
                                            <InputBox
                                                name={phone.v}
                                                type="text"
                                                id={phone.v}
                                                fullWidth
                                                onFocus={() => setCBData({ ...cbData, [phone.v]: `` })}
                                                helperText={errors[phone.v]}
                                                error={!!errors[phone.v]}
                                                value={cbData?.[phone.v]}
                                                sx={{
                                                    height: `48px`,
                                                    maxWidth: `unset`,
                                                    "& .MuiInputBase-root": { padding: `0 !important` },
                                                    input: { fontWeight: 500, height: `23px` },
                                                    marginTop: `5px`,
                                                }}
                                                onChange={v => {
                                                    setErrors({});
                                                    setCBData({
                                                        ...cbData,
                                                        [phone.v]: formatPhone(v.target.value.replace(/[^0-9.]/g, ``)),
                                                    });
                                                }}
                                            />
                                        </FormControl>
                                    ))}
                                <ModalButtonWrap>
                                    <SecondaryButton disabled={loading} onClick={closeAll}>
                                        Cancel
                                    </SecondaryButton>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={
                                            !cbData?.cashback ||
                                            (cbData.method === `PayPal` && !cbData.email) ||
                                            (cbData.method === `Venmo` && !cbData.phone) ||
                                            loading
                                        }
                                        onClick={() =>
                                            cashbackFieldRules.validator
                                                .validate(cbData)
                                                .then(() => setPanel(2))
                                                .catch((e: ValidationError) => {
                                                    setErrors(
                                                        e.params?.path
                                                            ? { [e.params.path as string]: e.message }
                                                            : { _form: e.message }
                                                    );
                                                })
                                        }
                                        sx={{
                                            background: buttonGradient(theme),
                                            padding: `4px 8px`,
                                            minWidth: `unset`,
                                            borderRadius: 2,
                                            "&.Mui-disabled": {
                                                background: `#999 !important`,
                                                opacity: 0.5,
                                            },
                                        }}
                                    >
                                        Next
                                    </Button>
                                </ModalButtonWrap>
                            </>
                        )}
                    </Panel>
                )}
                {panel === 2 && (
                    <Panel size={size} opacity={Number(panel === 2)}>
                        <Heading>Review Cash Back Request</Heading>
                        <TextLine sx={{ marginBottom: `55px` }}>
                            Your cash back will be delivered to your {cbData?.method} account on {context.nextFifteen}.
                        </TextLine>
                        <ConfirmBox size={size}>
                            <Box>
                                <LeftSide>Payout Method:</LeftSide>
                                <RightSide>
                                    <Box
                                        sx={{
                                            height: cbData?.method === `PayPal` ? 25 : 15,
                                            verticalAlign: `middle`,
                                            cursor: `pointer`,
                                        }}
                                        component="img"
                                        src={cbData?.method === `PayPal` ? PayPal : Venmo}
                                    />
                                </RightSide>
                            </Box>
                            <Box>
                                <LeftSide>Account:</LeftSide>
                                <RightSide>
                                    {cbData?.method === `PayPal` ? cbData?.email : formatPhone(cbData?.phone as string)}
                                </RightSide>
                            </Box>
                            <Box>
                                <LeftSide>Cash Back Amount:</LeftSide>
                                <RightSide>${cbData?.cashback}</RightSide>
                            </Box>
                        </ConfirmBox>
                        <ModalButtonWrap>
                            <SecondaryButton disabled={loading} onClick={() => setPanel(1)}>
                                Back
                            </SecondaryButton>
                            <Button
                                size="small"
                                disabled={loading}
                                variant="contained"
                                onClick={submitCashBack}
                                sx={{
                                    background: buttonGradient(theme),
                                    padding: `4px 8px`,
                                    minWidth: `unset`,
                                    borderRadius: 2,
                                    "&.Mui-disabled": {
                                        background: `#999 !important`,
                                        opacity: 0.5,
                                    },
                                }}
                            >
                                Submit
                            </Button>
                        </ModalButtonWrap>
                    </Panel>
                )}
                {panel === 3 && (
                    <Panel size={size} opacity={Number(panel === 3)}>
                        <Box my="24px" sx={{ textAlign: `center` }}>
                            {svgGradient(theme)}
                            <CheckCircleOutlineOutlined
                                fill="multiStopGradient"
                                sx={{ height: 80, width: 80, fill: `url(#multiStopGradient)` }}
                            />
                        </Box>
                        <Heading sx={{ textAlign: `center` }}>Cash Back Requested</Heading>
                        <TextLine my="24px" sx={{ textAlign: `center`, marginBottom: `60px` }}>
                            This payout has been added to your payout history. If you need to cancel this payout, you
                            may do so before the end of the month.
                        </TextLine>
                        <ModalButtonWrap sx={{ textAlign: `center` }}>
                            <LiLink to="/account/payouts">
                                <Button
                                    disabled={loading}
                                    size="small"
                                    variant="contained"
                                    sx={{
                                        background: buttonGradient(theme),
                                        padding: `4px 8px`,
                                        minWidth: `unset`,
                                        borderRadius: 2,
                                    }}
                                >
                                    View Payout History
                                </Button>
                            </LiLink>
                        </ModalButtonWrap>
                        <ModalButtonWrap sx={{ textAlign: `center` }}>
                            <Button
                                disabled={loading}
                                size="small"
                                variant="contained"
                                onClick={closeAll}
                                sx={{
                                    border: `1px solid ${theme.palette.account.accent}`,
                                    color: theme.palette.account.accent,
                                    background: `#FFF`,
                                    padding: `4px 8px`,
                                    minWidth: `unset`,
                                    borderRadius: 2,
                                    "&:hover": {
                                        color: `#FFF`,
                                    },
                                }}
                            >
                                Close
                            </Button>
                        </ModalButtonWrap>
                    </Panel>
                )}
            </Card>
        </AccountModal>
    );
};

export default RequestCashBack;
